import React from 'react'

import styled from 'styled-components'

import { Icon } from '_components/icon'
import {
  ClickableIconWrapper,
  IconWrapper as Wrapper,
} from '_components/icon/components'
import { getFontSize } from '_components/icon/functions'
import { palette } from '_styles/variables'

import { IconControllerProps, IconData } from '../helpers/types'

const Separator = styled.span`
  width: 1px;
  background-color: ${palette.neutral['grey-bbbdbf']};
  margin: 0 0.25rem;
`

const IconWrapper = styled.div<{
  placement?: 'right' | 'left'
  size?: number | string
  isClickable?: boolean
}>`
  position: absolute;
  display: flex;
  left: ${({ placement }) => (placement === 'left' ? '0.75rem' : 'unset')};
  right: ${({ placement }) => (placement === 'left' ? 'unset' : '0.75rem')};
  box-sizing: border-box;
  bottom:  50%;
  transform: translateY(50%);
  font-size: ${({ size }) => getFontSize(size)};
  color: ${palette.neutral['grey-icons']};

  &,
  & > ${Wrapper} {
    pointer-events: ${({ isClickable }) => (isClickable ? 'all' : 'none')};
  }

  & > ${ClickableIconWrapper} {
    pointer-events: all;
  }

  input:placeholder-shown ~ && [aria-label='clear'] {
    display: none;

    &~ ${Separator} {
      display: none;
    }
  }

  & [aria-label='clear'] {
    color: ${palette.neutral['grey-bbbdbf']};
  }
`


export const IconController: React.FC<IconControllerProps> = ({
  icon,
  renderIcon,
  iconGroup,
  clearable,
  onClear,
  onClickIcon,
  value,
  iconClassName,
  iconControllerClassName,
  iconPlacement = 'right',
  iconSize = '1.5em ',
  clearIcon = 'x-circle',
  ...iconProps
}) => {
  const group: IconData[] = []

  if (clearable) {
    group.push({
      icon: clearIcon,
      key: 'clear-button',
      onClick: onClear,
      onMouseDown: e => {
        e.preventDefault()
      },
      placement: 'right',
      size: '1.25rem',
      'aria-label': 'clear',
    })
  }

  if (iconGroup) {
    group.push(...iconGroup)
  }

  if (icon || renderIcon) {
    group.push({
      icon,
      renderIcon,
      key: 'single',
      placement: iconPlacement || 'right',
      onClick: onClickIcon,
      className: iconClassName,
      ...iconProps,
    } as IconData)
  }

  const leftGroup = group.filter(({ placement }) => placement === 'left')
  const rightGroup = group.filter(({ placement }) => placement === 'right')
  const isClickable = Boolean(onClickIcon || renderIcon)

  return (
    <>
      {leftGroup.length > 0 && (
        <IconWrapper
          size={iconSize}
          className={iconControllerClassName}
          placement="left"
          isClickable={isClickable}
          aria-hidden={!isClickable}
        >
          {leftGroup.map(({ key, ...props }, i) => {
            const { renderIcon: render } = props

            return (
              <React.Fragment key={key}>
                {i !== 0 && <Separator />}
                {render ? render() : <Icon aria-hidden={!isClickable} {...props} />}
              </React.Fragment>
            )
          })}
        </IconWrapper>
      )}
      {rightGroup.length > 0 && (
        <IconWrapper
          size={iconSize}
          className={iconControllerClassName}
          placement="right"
        >
          {rightGroup.map(({ key, ...props }, i) => {
            const { renderIcon: render } = props

            return (
              <React.Fragment key={key}>
                {i !== 0 && <Separator />}
                {render ? render() : <Icon {...props} />}
              </React.Fragment>
            )
          })}
        </IconWrapper>
      )}
    </>
  )
}

IconController.defaultProps = {
  iconGroup: [],
}
